// src/hooks/useStepHistory.tsx
import { useState } from "react";
var useStepHistory = (initialStep) => {
  const [step, setStep] = useState(initialStep);
  const [history, setHistory] = useState([step]);
  const atInitialStep = history.length === 1;
  return {
    reset: () => {
      setStep(initialStep);
      setHistory([initialStep]);
    },
    goToStep: (step2) => {
      setStep(step2);
      setHistory([...history, step2]);
    },
    currentStep: step,
    goBack: () => {
      if (atInitialStep) {
        return;
      }
      history.pop();
      const previous = history[history.length - 1];
      setHistory(history);
      setStep(previous);
    },
    atInitialStep,
    fullHistory: history
  };
};

// src/hooks/usePasswordValidation.tsx
import { List, ListIcon, ListItem, Text } from "@chakra-ui/react";
import { useEffect, useState as useState2 } from "react";
import { useFormContext } from "react-hook-form";
import { z } from "zod";

// src/assets/icons/CheckmarkIcon.tsx
import { Icon } from "@chakra-ui/react";
import { jsx, jsxs } from "react/jsx-runtime";
var CheckmarkIcon = (props) => /* @__PURE__ */ jsxs(
  Icon,
  {
    width: "18px",
    height: "18px",
    fill: "none",
    viewBox: "0 0 18 18",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      /* @__PURE__ */ jsx("g", { clipPath: "url(#clip0_415_12430)", children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M6 9.25L7.84615 11.25L12 6.75M15.75 9C15.75 12.7279 12.7279 15.75 9 15.75C5.27208 15.75 2.25 12.7279 2.25 9C2.25 5.27208 5.27208 2.25 9 2.25C12.7279 2.25 15.75 5.27208 15.75 9Z",
          stroke: "currentcolor",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          strokeWidth: "1.2"
        }
      ) }),
      /* @__PURE__ */ jsx("defs", { children: /* @__PURE__ */ jsx("clipPath", { id: "clip0_415_12430", children: /* @__PURE__ */ jsx("rect", { width: "18", height: "18", fill: "white" }) }) })
    ]
  }
);

// src/hooks/usePasswordValidation.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var PASSWORD_MIN_LENGTH = 12;
var getPasswordSchema = () => z.string().refine((value) => value.length >= PASSWORD_MIN_LENGTH, {
  path: ["minlength"]
}).refine((value) => /[A-Z]/.test(value), {
  path: ["uppercase"]
}).refine((value) => /\d/.test(value), {
  path: ["number"]
}).refine((value) => /[!@#$%^&*(),.?":{}|<>]/.test(value), {
  path: ["special"]
});
var PasswordStrengthBar = ({ requirements }) => /* @__PURE__ */ jsx2(List, { marginTop: "12px", spacing: "8px", children: requirements.map(({ message, path, passed }) => /* @__PURE__ */ jsxs2(
  ListItem,
  {
    alignItems: "flex-start",
    display: "flex",
    "data-testid": `${path}-${passed ? "passed" : "failed"}`,
    children: [
      /* @__PURE__ */ jsx2(ListIcon, { as: CheckmarkIcon, boxSize: "18px", color: passed ? "green" : "gray.400" }),
      /* @__PURE__ */ jsx2(Text, { color: "gray.700", size: "md", children: message })
    ]
  },
  path
)) });
var DEFAULT_REQUIREMENTS = [
  {
    message: `At least ${PASSWORD_MIN_LENGTH} characters long`,
    path: "minlength",
    passed: false
  },
  {
    message: "At least one uppercase letter",
    path: "uppercase",
    passed: false
  },
  {
    message: "At least one number",
    path: "number",
    passed: false
  },
  {
    message: 'At least one special character: !@#$%^&*(),.?":{}|<>',
    path: "special",
    passed: false
  }
  // TODO waiting for the design decision, https://linear.app/tezos/issue/UMA-1201/design-password-complexity
  // {
  //   message: "Good complexity, no simple sequences or patterns",
  //   path: "simplicity",
  //   passed: false,
  // },
];
var usePasswordValidation = ({
  inputName = "password"
} = {}) => {
  const [requirements, setRequirements] = useState2(DEFAULT_REQUIREMENTS);
  const {
    formState: { errors, isDirty }
  } = useFormContext();
  const passwordError = errors[inputName];
  const hasRequiredError = passwordError?.type === "required";
  useEffect(() => {
    if (hasRequiredError || !isDirty) {
      setRequirements(DEFAULT_REQUIREMENTS);
    }
  }, [isDirty, hasRequiredError]);
  const validatePasswordStrength = (value) => {
    try {
      getPasswordSchema().parse(value);
      setRequirements(requirements.map((requirement) => ({ ...requirement, passed: true })));
    } catch (e) {
      if (e instanceof z.ZodError) {
        const errorPaths = new Set(e.errors.map((error) => error.path[0]));
        setRequirements(
          (prev) => prev.map((requirement) => ({
            ...requirement,
            passed: !errorPaths.has(requirement.path)
          }))
        );
        return false;
      }
    }
    return true;
  };
  return {
    validatePasswordStrength,
    PasswordStrengthBar: /* @__PURE__ */ jsx2(PasswordStrengthBar, { requirements })
  };
};

// src/hooks/useToggleMnemonic.tsx
import { useEffect as useEffect2, useRef, useState as useState3 } from "react";
var MNEMONIC_VISIBILITY_TIMEOUT = 3e4;
var useToggleMnemonic = (timeout = MNEMONIC_VISIBILITY_TIMEOUT) => {
  const [isVisible, setIsVisible] = useState3(false);
  const timerRef = useRef();
  const resetIsVisible = () => {
    setIsVisible(false);
  };
  useEffect2(() => {
    if (isVisible) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(resetIsVisible, timeout);
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isVisible, timeout]);
  return { isVisible, toggleMnemonic: () => setIsVisible((prev) => !prev) };
};

// src/utils/validationSchemes.ts
import { PROHIBITED_CHARACTERS } from "@umami/state";
import { z as z2 } from "zod";
var URL_REGEX = /^(https?:\/\/)?(www\.)?([a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+)(\/[a-zA-Z0-9_-]+)*(\/?|\?[a-zA-Z0-9_-]+=?[a-zA-Z0-9_-]*(&[a-zA-Z0-9_-]+=?[a-zA-Z0-9_-]*)*)?(#[a-zA-Z0-9_-]+)?$/;
var urlScheme = (urlType) => z2.string().min(1, `${urlType} URL is required`).regex(URL_REGEX, `Enter a valid ${urlType} URL`).startsWith("https://", { message: `${urlType} URL must be secure and start with 'https://'` });
var getNetworkValidationScheme = (availableNetworks, network) => z2.object({
  name: network ? z2.string().optional() : z2.string().min(1, "Name is required").max(256, "Name should not exceed 256 characters").refine((name) => !PROHIBITED_CHARACTERS.some((char) => name.includes(char)), {
    message: "Name contains special character(s)"
  }).refine((name) => !availableNetworks.find((n) => n.name === name), {
    message: "Network with this name already exists"
  }),
  rpcUrl: urlScheme("RPC"),
  tzktApiUrl: urlScheme("Tzkt API"),
  explorerUrl: urlScheme("Tzkt Explorer"),
  buyTezUrl: urlScheme("Buy tez").or(z2.literal(""))
});

// src/components/DynamicDisclosure/DynamicDisclosure.tsx
import {
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Modal,
  ModalOverlay
} from "@chakra-ui/react";
import { cloneDeep, merge } from "lodash";
import {
  createContext,
  useContext,
  useRef as useRef2,
  useState as useState4
} from "react";
import { RemoveScroll } from "react-remove-scroll";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var defaultContextValue = {
  openWith: async () => {
  },
  onClose: () => {
  },
  goBack: () => {
  },
  goToIndex: () => {
  },
  isOpen: false,
  hasPrevious: false,
  canBeOverridden: false,
  formValues: {},
  allFormValues: { current: {} },
  isInitializing: false
};
var DynamicModalContext = createContext(defaultContextValue);
var DynamicDrawerContext = createContext(defaultContextValue);
var useDynamicModalContext = () => useContext(DynamicModalContext);
var useDynamicDrawerContext = () => useContext(DynamicDrawerContext);
var useDynamicDisclosure = () => {
  const stackRef = useRef2([]);
  const [currentIndex, setCurrentIndex] = useState4(-1);
  const openWith = async (content, props = {}) => {
    stackRef.current.push({
      content,
      props,
      formValues: {}
    });
    setCurrentIndex((current) => current + 1);
    return Promise.resolve();
  };
  const currentItem = stackRef.current.at(currentIndex) || null;
  const onClose = () => {
    setCurrentIndex(-1);
    stackRef.current = [];
    return currentItem?.props.onClose?.();
  };
  const goBack = (payload) => {
    if (payload instanceof Event || payload && payload.nativeEvent) {
      payload = void 0;
    }
    const previousIndex = currentIndex - 1;
    if (payload && stackRef.current[previousIndex]) {
      stackRef.current[previousIndex].formValues = {
        ...stackRef.current[previousIndex].formValues,
        ...payload
      };
    }
    setCurrentIndex(previousIndex);
    stackRef.current.pop();
  };
  const goToIndex = (index = -1) => {
    if (index >= 0 && index < stackRef.current.length) {
      const itemsToRemove = stackRef.current.length - index - 1;
      stackRef.current.splice(index + 1, itemsToRemove);
      setCurrentIndex(index);
    } else {
      setCurrentIndex((current) => current - 1);
      stackRef.current.pop();
    }
  };
  const allFormValues = useRef2({});
  allFormValues.current = stackRef.current.map((item) => item.formValues).reduce((acc, curr) => merge(acc, cloneDeep(curr)), {});
  return {
    isOpen: !!currentItem,
    onClose,
    openWith,
    goBack,
    goToIndex,
    content: currentItem?.content,
    props: currentItem?.props || {},
    formValues: currentItem?.formValues || {},
    hasPrevious: stackRef.current.length > 1,
    allFormValues,
    canBeOverridden: !!currentItem?.props.canBeOverridden,
    closeOnOverlayClick: !!currentItem?.props.closeOnOverlayClick,
    isInitializing: true
  };
};
var useDynamicModal = () => {
  const disclosure = useDynamicDisclosure();
  const { isOpen, props, content, onClose, closeOnOverlayClick } = disclosure;
  return {
    ...disclosure,
    content: /* @__PURE__ */ jsxs3(
      Modal,
      {
        autoFocus: false,
        blockScrollOnMount: false,
        closeOnOverlayClick,
        isCentered: true,
        isOpen,
        motionPreset: "slideInBottom",
        onClose,
        ...props,
        children: [
          /* @__PURE__ */ jsx3(ModalOverlay, {}),
          /* @__PURE__ */ jsx3(RemoveScroll, { enabled: isOpen, children: content })
        ]
      }
    )
  };
};
var useDynamicDrawer = () => {
  const disclosure = useDynamicDisclosure();
  const { isOpen, content, props, onClose } = disclosure;
  return {
    ...disclosure,
    content: /* @__PURE__ */ jsxs3(Drawer, { isOpen, onClose, placement: "right", ...props, children: [
      /* @__PURE__ */ jsx3(DrawerOverlay, {}),
      /* @__PURE__ */ jsx3(DrawerContent, { children: content })
    ] })
  };
};
var DynamicDisclosureProvider = ({ children }) => {
  const modalDisclosure = useDynamicModal();
  const drawerDisclosure = useDynamicDrawer();
  return /* @__PURE__ */ jsx3(DynamicModalContext.Provider, { value: modalDisclosure, children: /* @__PURE__ */ jsxs3(DynamicDrawerContext.Provider, { value: drawerDisclosure, children: [
    children,
    drawerDisclosure.content,
    modalDisclosure.content
  ] }) });
};

// src/components/DynamicDisclosure/useMultiForm.tsx
import merge2 from "lodash/merge";
import {
  useForm
} from "react-hook-form";
var useMultiForm = ({
  usedIn,
  ...props
}) => {
  const modalContext = useDynamicModalContext();
  const drawerContext = useDynamicDrawerContext();
  const formDefaultValues = usedIn === "drawer" ? drawerContext.formValues : modalContext.formValues;
  const form = useForm({
    ...props,
    defaultValues: { ...props.defaultValues, ...formDefaultValues }
  });
  const handleSubmit = (onValid, onInvalid) => form.handleSubmit(
    (submittedValues) => {
      merge2(formDefaultValues, submittedValues);
      return onValid(submittedValues);
    },
    onInvalid
  );
  return {
    ...form,
    handleSubmit
  };
};

// src/components/AddressPill/useAddressPill.ts
import { useDisclosure, useOutsideClick } from "@chakra-ui/react";
import { parsePkh } from "@umami/chains";
import { useCallback, useRef as useRef3, useState as useState5 } from "react";

// src/components/AddressPill/useAddressKind.ts
import { ChainFullIds } from "@umami/chains";
import {
  useGetBaker,
  useGetContactName,
  useGetOwnedAccountSafe,
  useGetTokenType,
  useGetTzAddressBySaplingAddress,
  useSelectedNetwork
} from "@umami/state";
var useAddressKind = (address, chainId = ChainFullIds.TEZOS) => {
  const isSaplingAddress = address.type === "sapling";
  const tzAddressPkh = useGetTzAddressBySaplingAddress(address.pkh);
  const tzAddress = tzAddressPkh ? { type: "implicit", pkh: tzAddressPkh } : address;
  const ownedAccount = useOwnedAccountAddressKind(tzAddress, chainId);
  const token = useTokenAddressKind(address);
  const baker = useBakerAddressKind(address);
  const contact = useContactAddressKind(address);
  const sapling = {
    pkh: address.pkh,
    type: "sapling",
    label: ownedAccount?.label ?? contact?.label ?? null
  };
  const known = isSaplingAddress ? sapling : ownedAccount || token || baker || contact;
  const result = known || {
    pkh: address.pkh,
    type: "unknown",
    label: null
  };
  return { ...result, chainId };
};
var useBakerAddressKind = ({ pkh }) => {
  const getBaker = useGetBaker();
  const baker = getBaker(pkh);
  return baker ? {
    pkh,
    type: "baker",
    label: baker.name
  } : null;
};
var useOwnedAccountAddressKind = (address, chainId) => {
  const getOwnedAccount = useGetOwnedAccountSafe();
  const account = getOwnedAccount(address.pkh, chainId);
  return account ? {
    type: account.type,
    pkh: address.pkh,
    label: account.label
  } : null;
};
var useTokenAddressKind = ({ pkh }) => {
  const network = useSelectedNetwork();
  const getTokenType = useGetTokenType(network);
  const tokenType = getTokenType(pkh);
  if (!tokenType) {
    return null;
  }
  switch (tokenType) {
    case "fa1.2":
      return {
        pkh,
        type: "fa1.2",
        label: null
      };
    case "fa2":
    case "nft":
      return {
        pkh,
        type: "fa2",
        label: null
      };
  }
};
var useContactAddressKind = ({ pkh }) => {
  const getContactName = useGetContactName();
  const contactName = getContactName(pkh);
  if (!contactName) {
    return null;
  }
  return {
    pkh,
    type: "contact",
    label: contactName
  };
};

// src/components/AddressPill/useAddressPill.ts
var useAddressPill = ({
  rawAddress,
  mode = "default"
}) => {
  let address;
  let addressAlias;
  if ("pkh" in rawAddress && "type" in rawAddress) {
    address = rawAddress;
    addressAlias = void 0;
  } else {
    address = parsePkh(rawAddress.address);
    addressAlias = rawAddress.alias || void 0;
  }
  const addressKind = useAddressKind(address);
  const showIcons = mode !== "no_icons";
  const [isMouseHover, setIsMouseHover] = useState5(false);
  const { onOpen, onClose: closePopover, isOpen: isPopoverOpen } = useDisclosure();
  const onClick = useCallback(async () => {
    await navigator.clipboard.writeText(address.pkh);
    onOpen();
    setTimeout(closePopover, 1e3);
  }, [onOpen, address.pkh, closePopover]);
  const elementRef = useRef3(null);
  useOutsideClick({
    ref: elementRef,
    handler: () => setIsMouseHover(false)
  });
  return {
    addressKind,
    addressAlias,
    address,
    isMouseHover,
    setIsMouseHover,
    isPopoverOpen,
    closePopover,
    elementRef,
    showIcons,
    onClick
  };
};

// src/components/AddressPill/types.ts
var KNOWN_ADDRESS_TYPES = [
  "mnemonic",
  "social",
  "secret_key",
  "ledger",
  "multisig",
  "baker"
];

// src/components/AddressPill/AddressPillText.tsx
import { Text as Text2 } from "@chakra-ui/react";
import { useGetContactName as useGetContactName2 } from "@umami/state";
import { formatPkh, truncate } from "@umami/tezos";
import { jsx as jsx4 } from "react/jsx-runtime";
var AddressPillText = ({
  addressKind: { pkh, label },
  showPkh,
  alias,
  ...props
}) => {
  const getContactName = useGetContactName2();
  const formattedPkh = formatPkh(pkh);
  const nameOrLabel = getContactName(pkh) || label || alias;
  if (showPkh) {
    return /* @__PURE__ */ jsx4(Text2, { ...props, children: formattedPkh });
  }
  return /* @__PURE__ */ jsx4(Text2, { overflow: "hidden", textOverflow: "ellipsis", ...props, children: nameOrLabel ? truncate(nameOrLabel, 50) : formattedPkh });
};

// src/components/AddressPill/testUtils.ts
import { mockContractAddress } from "@umami/chains";
var mockFA2AddressKind = (index) => ({
  type: "fa2",
  pkh: mockContractAddress(index || 0).pkh,
  label: null
});

// src/components/MnemonicAutocomplete/MnemonicAutocomplete.tsx
import { Input, ListItem as ListItem2, UnorderedList } from "@chakra-ui/react";
import { wordlists } from "bip39";
import { useState as useState6 } from "react";
import { useFormContext as useFormContext2 } from "react-hook-form";
import { Fragment, jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var MnemonicAutocomplete = ({
  inputName,
  validate,
  inputProps,
  listProps
}) => {
  const [hidden, setHidden] = useState6(true);
  const { register, setValue, watch } = useFormContext2();
  const value = watch(inputName);
  const matching = wordlists.EN.filter((word) => value && word.startsWith(value)).sort();
  const showSuggestions = !hidden && matching.length > 0 && // if we found a single match there is no need in the suggestions
  (matching.length > 1 || matching[0] !== value);
  return /* @__PURE__ */ jsxs4(Fragment, { children: [
    /* @__PURE__ */ jsx5(
      Input,
      {
        height: "full",
        autoComplete: "off",
        "data-testid": "mnemonic-input",
        onFocus: () => setHidden(false),
        placeholder: "Type here...",
        ...register(inputName, {
          required: "Required",
          validate,
          onChange: () => setHidden(false),
          onBlur: () => setHidden(true)
        }),
        ...inputProps
      }
    ),
    showSuggestions && /* @__PURE__ */ jsx5(UnorderedList, { "data-testid": "suggestions", variant: "suggestions", ...listProps, children: matching.map((word) => /* @__PURE__ */ jsx5(
      ListItem2,
      {
        zIndex: 1,
        paddingTop: "2px",
        fontSize: "sm",
        fontWeight: 600,
        "data-testid": "suggestion",
        onMouseDown: () => setValue(inputName, word),
        paddingX: "7px",
        children: word
      },
      word
    )) })
  ] });
};

// src/components/ReactIdenticon/ReactIdenticon.tsx
import md5 from "md5";
import { memo, useEffect as useEffect3, useRef as useRef4 } from "react";
import { jsx as jsx6 } from "react/jsx-runtime";
var ReactIdenticon = memo(({ count = 5, background, string, size, ...rest }) => {
  const canvasRef = useRef4(null);
  useEffect3(() => {
    updateCanvas();
  });
  const updateCanvas = () => {
    const hash = md5(string);
    const block = Math.floor(size / count);
    const hashcolor = hash.slice(0, 6);
    const canvas = canvasRef.current;
    canvas.width = block * count;
    canvas.height = block * count;
    const arr = hash.split("").map((el) => parseInt(el, 16)).map((n) => n < 8 ? 0 : 1);
    const map = [];
    map[0] = map[4] = arr.slice(0, 5);
    map[1] = map[3] = arr.slice(5, 10);
    map[2] = arr.slice(10, 15);
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    map.forEach((row, i) => {
      row.forEach((el, j) => {
        if (el) {
          ctx.fillStyle = "#" + hashcolor;
          ctx.fillRect(block * i, block * j, block, block);
        } else {
          ctx.fillStyle = background;
          ctx.fillRect(block * i, block * j, block, block);
        }
      });
    });
  };
  return /* @__PURE__ */ jsx6("canvas", { ref: canvasRef, style: { width: size, height: size }, ...rest });
});
export {
  AddressPillText,
  DynamicDisclosureProvider,
  DynamicDrawerContext,
  DynamicModalContext,
  KNOWN_ADDRESS_TYPES,
  MnemonicAutocomplete,
  PASSWORD_MIN_LENGTH,
  ReactIdenticon,
  getNetworkValidationScheme,
  mockFA2AddressKind,
  useAddressKind,
  useAddressPill,
  useDynamicDisclosure,
  useDynamicDrawer,
  useDynamicDrawerContext,
  useDynamicModal,
  useDynamicModalContext,
  useMultiForm,
  usePasswordValidation,
  useStepHistory,
  useToggleMnemonic
};
